import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CloseButton, Group, NavLink, Text } from '@mantine/core';

/**
 * ОТКРЫТЫЕ — КАК ПАНЕЛЬ ОКОН В 1С (12.10.2026). Отдел документов: «в 1С
 * вкладки сохраняются: пока я их не закрою, они висят, нужные у меня
 * открыты; вот сюда бы вывести все незакрытые наши документы, чтобы я не
 * делала все эти действия по новой». В CRM, уйдя из «Денег по адресам» в
 * «Монтаж» поправить работы, человек возвращался в пустой экран: снова месяц,
 * снова бригада, снова адрес.
 *
 * Здесь каждая открытая страница встаёт строкой под меню и висит, пока её не
 * закроют крестиком. Нажатие возвращает туда, где человек был: адрес вместе
 * с параметрами — вкладка, день, бригада. Одна строка на страницу: открыли
 * ту же страницу с другим днём — строка помнит последний.
 *
 * Название страница может назвать сама (`useOpenPageTitle`: «Деньги по
 * адресам · Гурин · 12.10»), иначе берётся пункт меню. Список живёт на
 * устройстве у каждого своим (память браузера — удобство, а не данные):
 * недоступна память — панели просто нет.
 */
export interface OpenPage {
  /** Страница — адрес без параметров: по нему строка одна */
  key: string;
  /** Куда вернуться — с параметрами */
  path: string;
  title: string;
}

const MAX_PAGES = 15;
const storageKey = (userId: string) => `blagodar.openPages.${userId}`;

/** Названия, которые страницы дали себе сами, — по адресу без параметров */
const pageTitles = new Map<string, string>();
const titleListeners = new Set<() => void>();

/**
 * Страница называет себя для панели «Открытые». `active` — пока вкладка
 * страницы на экране: спрятанная вкладка своё название не навязывает.
 */
export function useOpenPageTitle(title: string | null | undefined, active = true): void {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!active || !title) return;
    pageTitles.set(pathname, title);
    titleListeners.forEach((l) => l());
    return () => {
      if (pageTitles.get(pathname) === title) {
        pageTitles.delete(pathname);
        titleListeners.forEach((l) => l());
      }
    };
  }, [pathname, title, active]);
}

function load(userId: string): OpenPage[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const list = raw ? (JSON.parse(raw) as OpenPage[]) : [];
    return Array.isArray(list) ? list.filter((p) => p && typeof p.key === 'string' && typeof p.path === 'string') : [];
  } catch {
    return [];
  }
}

function save(userId: string, pages: OpenPage[]): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(pages));
  } catch {
    // Память недоступна — панель живёт до перезагрузки
  }
}

/** Вкладки «Монтажников» — по ним страница называется точнее, чем пунктом меню */
const INSTALLER_TABS: Record<string, string> = {
  occupancy: 'Занятость по дням',
  summary: 'Все бригады разом',
  money: 'Деньги по адресам',
  brigades: 'Бригады и состав',
  absences: 'Кто не работает',
  salaries: 'Оклады и доли',
  payroll: 'Правила зарплаты',
  shifts: 'Удержания за смену',
  works: 'Работы и ставки',
};

/** Страница с номером в адресе — что это, словом, пока она не назвала себя сама */
const DETAIL_TITLES: Array<[RegExp, string]> = [
  [/^\/deals\/[^/]+$/, 'Сделка'],
  [/^\/deals\/[^/]+\/\w+/, 'Лист сделки'],
  [/^\/consumables\/new$/, 'Новая расходка'],
  [/^\/consumables\/[^/]+\/sheet$/, 'Лист расходки'],
  [/^\/consumables\/[^/]+$/, 'Расходка'],
  [/^\/measurements\/[^/]+\/\w+/, 'Лист замера'],
  [/^\/measurements\/[^/]+$/, 'Замер'],
  [/^\/clients\/[^/]+$/, 'Клиент'],
  [/^\/design\/[^/]+\/sheet$/, 'Лист проекта'],
  [/^\/design\/[^/]+$/, 'Проект потолка'],
  [/^\/installations\/[^/]+\/act$/, 'Акт монтажа'],
  [/^\/installations\/day-sheet$/, 'Лист на день'],
  [/^\/production\/[^/]+\/sheet$/, 'Лист в цех'],
  [/^\/payroll\/slip/, 'Расчётный листок'],
];

/** Название страницы по адресу: пункт меню и, где есть, вкладка */
export function openPageTitle(pathname: string, search: string, menu: Array<{ to: string; label: string }>): string {
  for (const [re, title] of DETAIL_TITLES) if (re.test(pathname)) return title;
  const item = [...menu]
    .filter((m) => pathname === m.to || pathname.startsWith(`${m.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
  const base = item?.label ?? pathname;
  if (pathname === '/installers') {
    const tab = new URLSearchParams(search).get('tab') ?? 'occupancy';
    return INSTALLER_TABS[tab] ? `${base} · ${INSTALLER_TABS[tab]}` : base;
  }
  return base;
}

/**
 * Список открытых страниц человека: каждый переход добавляет страницу или
 * обновляет её строку, крестик убирает.
 */
export function useOpenPages(userId: string | null | undefined, menu: Array<{ to: string; label: string }>) {
  const location = useLocation();
  const [pages, setPages] = useState<OpenPage[]>(() => (userId ? load(userId) : []));
  const [, setTick] = useState(0);

  useEffect(() => {
    setPages(userId ? load(userId) : []);
  }, [userId]);

  // Страница назвала себя — строка панели берёт это название
  useEffect(() => {
    const listener = () => setTick((t) => t + 1);
    titleListeners.add(listener);
    return () => {
      titleListeners.delete(listener);
    };
  }, []);

  const key = location.pathname;
  const path = `${location.pathname}${location.search}`;
  const ownTitle = pageTitles.get(key);
  const menuKey = menu.map((m) => m.to).join('|');

  useEffect(() => {
    if (!userId || key === '/login') return;
    setPages((prev) => {
      const title = ownTitle ?? openPageTitle(key, location.search, menu);
      const i = prev.findIndex((p) => p.key === key);
      let next: OpenPage[];
      if (i >= 0) {
        if (prev[i]!.path === path && prev[i]!.title === title) return prev;
        next = prev.map((p, j) => (j === i ? { key, path, title } : p));
      } else {
        next = [...prev, { key, path, title }];
        // Больше предела — уходит самая давняя, но не та, что на экране
        while (next.length > MAX_PAGES) next.splice(next.findIndex((p) => p.key !== key), 1);
      }
      save(userId, next);
      return next;
    });
    // menu — по составу пунктов, а не по новому массиву на каждой отрисовке
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, key, path, ownTitle, menuKey]);

  const close = useCallback(
    (closeKey: string) => {
      if (!userId) return;
      setPages((prev) => {
        const next = prev.filter((p) => p.key !== closeKey);
        save(userId, next);
        return next;
      });
    },
    [userId],
  );

  return { pages, close, currentKey: key };
}

/** Строки панели под меню: нажатие возвращает, крестик закрывает */
export function OpenPagesList({
  pages,
  currentKey,
  onClose,
  onNavigate,
}: {
  pages: OpenPage[];
  currentKey: string;
  onClose: (key: string) => void;
  onNavigate?: () => void;
}) {
  if (pages.length === 0) return null;
  return (
    <div data-testid="open-pages">
      <Group justify="space-between" px="xs" pt="md" pb={4}>
        <Text size="xs" tt="uppercase" fw={700} c="dimmed">
          Открытые
        </Text>
      </Group>
      {pages.map((p) => (
        <NavLink
          key={p.key}
          component={Link}
          to={p.path}
          active={p.key === currentKey}
          variant="light"
          onClick={onNavigate}
          label={
            <Text size="sm" lineClamp={2}>
              {p.title}
            </Text>
          }
          rightSection={
            <CloseButton
              size="sm"
              aria-label={`Закрыть «${p.title}»`}
              onClick={(e) => {
                // Крестик закрывает строку, а не открывает страницу
                e.preventDefault();
                e.stopPropagation();
                onClose(p.key);
              }}
            />
          }
          py={4}
        />
      ))}
    </div>
  );
}
